<template>
  <ds-card
    title="Recurring annual blocks"
    subtitle="Blocks that repeat on the same date every year, such as annual closures. Pick a month and day, name the block, and save. It applies automatically each year."
  >
    <div
      v-for="(block, index) in blocks"
      :key="block.id || `new-${index}`"
      class="lrb-block"
    >
      <div class="lrb-row">
        <div class="lrb-date">
          <ds-select
            v-model="block.month"
            :options="monthOptions"
            number
            aria-label="Month"
            @change="onChange(index)"
          />
          <ds-select
            v-model="block.day"
            :options="dayOptions(block.month)"
            number
            aria-label="Day"
            @change="onChange(index)"
          />
        </div>

        <ds-input
          v-model="block.name"
          class="lrb-name"
          placeholder="Name (e.g. Staff Meeting)"
          @input="onChange(index)"
        />

        <div class="lrb-actions">
          <ds-button
            v-if="canSave(block, index)"
            size="sm"
            :disabled="block.saving"
            @click="save(index)"
          >
            {{ block.saving ? 'Saving…' : 'Save' }}
          </ds-button>
          <ds-button
            variant="danger"
            size="sm"
            :disabled="block.saving"
            @click="remove(index)"
          >
            Remove
          </ds-button>
        </div>
      </div>

      <div
        v-if="block.dirty && isDuplicate(block, index)"
        class="lrb-error"
        role="alert"
      >
        A recurring block already exists for this date.
      </div>
    </div>

    <p
      v-if="blocks.length === 0"
      class="lrb-empty"
    >
      No recurring annual blocks yet.
    </p>

    <ds-button
      variant="secondary"
      @click="add"
    >
      + Add recurring block
    </ds-button>
  </ds-card>
</template>

<script>

import { event } from 'vue-gtag'
import {
  DsButton, DsCard, DsInput, DsSelect,
} from '@/design-system/components'
import { LocationRecurringBlock } from '@/_models/location-recurring-block'
import { monthNames } from '@/_helpers/util'

// Days per month. February allows 29 so leap-year dates can be blocked.
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

export default {
  name: 'LocationRecurringBlocks',
  components: {
    DsButton,
    DsCard,
    DsInput,
    DsSelect,
  },
  props: {
    // Embedded in the location object (via v-model from Preferences), like overrides.
    value: {
      type: Array,
      default: () => [],
    },
    locationId: {
      type: String,
      default: () => '',
    },
  },
  data() {
    return {
      blocks: [],
      monthOptions: monthNames.map((text, i) => ({ value: i + 1, text })),
    }
  },
  watch: {
    // Re-sync rows when the partner/location (and its embedded list) changes.
    value: {
      handler() {
        this.setData()
      },
    },
  },
  created() {
    this.setData()
  },
  methods: {
    // Copy the embedded annual patterns into local working rows with UI flags.
    setData() {
      this.blocks = this.value.filter(block => block.patternType === 'annual'
        && Number.isInteger(block.month)
        && Number.isInteger(block.day))
      this.blocks.forEach(block => {
        if (block.dirty === undefined) this.$set(block, 'dirty', false)
        if (block.saving === undefined) this.$set(block, 'saving', false)
        // Snapshot persisted values so a rejected edit can be rolled back; don't
        // overwrite the snapshot while the row has unsaved edits.
        if (!block.dirty) {
          block.original = { month: block.month, day: block.day, name: block.name }
        }
        block.locationId = this.locationId
      })
    },
    daysInMonth(month) {
      return DAYS_IN_MONTH[(month || 1) - 1] || 31
    },
    dayOptions(month) {
      const days = this.daysInMonth(month)
      return Array.from({ length: days }, (_, i) => ({ value: i + 1, text: i + 1 }))
    },
    add() {
      this.blocks.push({
        id: null,
        locationId: this.locationId,
        month: 1,
        day: 1,
        name: '',
        patternType: 'annual',
        dirty: true,
        saving: false,
      })
      this.$emit('input', this.blocks)
    },
    onChange(index) {
      const block = this.blocks[index]
      // Clamp the day if the selected month has fewer days.
      const maxDay = this.daysInMonth(block.month)
      if (block.day > maxDay) {
        this.$set(this.blocks[index], 'day', maxDay)
      }
      this.$set(this.blocks[index], 'dirty', true)
    },
    isDuplicate(block, index) {
      if (!block.month || !block.day) {
        return false
      }
      return this.blocks.some((other, i) => i !== index
        && other.month === block.month
        && other.day === block.day)
    },
    canSave(block, index) {
      return block.dirty
        && !!block.month
        && !!block.day
        && !!(block.name && block.name.trim())
        && !this.isDuplicate(block, index)
        && !block.saving
    },
    // Surfaces the backend's error message (falls back to a generic one) as a toast.
    showError(error, fallback) {
      const message = (error && (
        (error.errorData && error.errorData.message)
        || error.message
        || (error.data && error.data.message)
      )) || (typeof error === 'string' ? error : fallback)
      this.$notify.error(`${message}`, { noAutoHide: true })
    },
    save(index) {
      const block = this.blocks[index]
      if (!this.canSave(block, index)) {
        return
      }
      this.$set(block, 'saving', true)

      // model.save() routes to create() for a new row (no id) or update() for an
      // existing one. Mutate `block` (not blocks[index]) so a concurrent removal
      // that shifts indices can't make us update the wrong row.
      const model = new LocationRecurringBlock(
        block.id,
        this.locationId,
        block.month,
        block.day,
        block.name.trim(),
      )
      model.save().then(() => {
        this.$set(block, 'id', model.id)
        this.$set(block, 'name', model.name)
        this.$set(block, 'dirty', false)
        this.$set(block, 'saving', false)
        this.$emit('input', this.blocks)
        event('save-blocked-day-pattern', { monthDay: model.toMonthDay(), name: model.name })
      }).catch(error => {
        this.$set(block, 'saving', false)
        // A rejected update didn't persist (e.g. a default pattern), so roll the row
        // back to its last-saved values. New rows have no snapshot, so they're kept.
        if (block.original) {
          this.$set(block, 'month', block.original.month)
          this.$set(block, 'day', block.original.day)
          this.$set(block, 'name', block.original.name)
          this.$set(block, 'dirty', false)
        }
        this.showError(error, 'Could not save the recurring block. Please try again.')
      })
    },
    remove(index) {
      const block = this.blocks[index]
      // eslint-disable-next-line no-alert
      if (!window.confirm('Are you sure you want to delete this recurring block?')) {
        return
      }

      // Unsaved row (never persisted): just drop it locally, nothing to delete.
      if (!block.id) {
        this.blocks.splice(index, 1)
        this.$emit('input', this.blocks)
        return
      }

      this.$set(block, 'saving', true)
      const model = new LocationRecurringBlock(block.id, this.locationId)
      model.deleteThis().then(() => {
        const i = this.blocks.indexOf(block)
        if (i !== -1) {
          this.blocks.splice(i, 1)
        }
        this.$emit('input', this.blocks)
      }).catch(error => {
        this.$set(block, 'saving', false)
        this.showError(error, 'Could not delete the recurring block. Please try again.')
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/design-system/mixins';

.lrb-block {
  @include ds-inset-row;

  padding: 14px 18px;
  margin-bottom: 10px;
}

.lrb-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.lrb-date {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;

  ::v-deep .ds-select:first-child { width: 148px; }

  ::v-deep .ds-select:last-child { width: 84px; }
}

.lrb-name {
  flex: 1 1 200px;
  min-width: 0;
}

.lrb-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: auto;
}

.lrb-error {
  @include ds-error-text;

  margin-top: 8px;
}

.lrb-empty {
  margin: 0 0 14px;
  font-size: 12.5px;
  font-style: italic;
  color: $c-text-sec;
}
</style>
